import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Tooltip from "@material-ui/core/Tooltip";

import Dialog from "src/components/Dialog";
import Autocomplete from "src/components/Autocomplete";
import SalePlatformField from "src/components/SalePlatformField";
import LoadableButton from "src/components/LoadableButton";
import { AddGeneralLedgerAccountDialog } from "src/pages/RootDialogs/AddGeneralLedgerAccountDialog";
import VendorDialog from "src/pages/RootDialogs/ProfileSettings/VendorDialog";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import { getVendors } from "src/store/vendor/selector";
import {
  batchUpdateTransactions,
  type BatchTransactionUpdate,
} from "src/apiService/modules/transactions";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import type { GeneralLedgerAccountV2 } from "src/interfaces/generalLedgerAccount.interface";

const ADD_NEW_VENDOR_ID = "add-new-vendor";
const NO_VENDOR_ID = "default";

interface VendorOption {
  id: string;
  name: string;
}

const DISABLED_SUBTYPES = [
  "credit-card",
  "bank-account-cash",
  "loan-line-of-credit",
];

const isDisabledAccount = (account: GeneralLedgerAccountV2) => {
  const subType = account.type?.[1];
  return DISABLED_SUBTYPES.includes(subType as string);
};

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    paddingTop: theme.spacing(1),
  },
  header: {
    display: "flex",
    justifyContent: "flex-start",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    paddingTop: theme.spacing(0.5),
  },
  disabledOption: {
    color: theme.palette.text.disabled,
    width: "100%",
    pointerEvents: "auto" as const,
  },
  notice: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
  },
}));

export interface BulkEditPanelProps {
  open: boolean;
  onClose: () => void;
  selectedTransactionIds: string[];
  readOnlyCount?: number;
  onOpenCSVBulkEdit: () => void;
}

function BulkEditPanelContent({
  onClose,
  selectedTransactionIds,
  readOnlyCount = 0,
  onOpenCSVBulkEdit,
}: Omit<BulkEditPanelProps, "open">) {
  const classes = useStyles();
  const recordEvent = useRecordEvent();
  const count = selectedTransactionIds.length;

  const [selectedAccount, setSelectedAccount] =
    useState<GeneralLedgerAccountV2 | null>(null);
  const [addAccountDialogOpen, setAddAccountDialogOpen] = useState(false);

  const [selectedVendorId, setSelectedVendorId] = useState<string | undefined>(
    undefined
  );
  const [vendorTouched, setVendorTouched] = useState(false);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);

  const [selectedBankAccount, setSelectedBankAccount] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const vendors = useSelector(getVendors);
  const vendorOptions = useMemo<VendorOption[]>(() => {
    const sorted = vendors
      ? [...vendors].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      : [];
    return [
      { id: ADD_NEW_VENDOR_ID, name: "Add new vendor" },
      ...sorted.map((v) => ({ id: v.id, name: v.name })),
      { id: NO_VENDOR_ID, name: "No vendor" },
    ];
  }, [vendors]);
  const selectedVendor = useMemo<VendorOption | null>(() => {
    if (!vendorTouched) return null;
    const targetId = selectedVendorId ?? NO_VENDOR_ID;
    return vendorOptions.find((opt) => opt.id === targetId) ?? null;
  }, [vendorTouched, selectedVendorId, vendorOptions]);

  const generalLedgerAccounts = useSelector(generalLedgerAccountsSelector);

  const glOptions = useMemo(() => {
    let hasBalanceTransfer = false;

    const accounts = [...generalLedgerAccounts].filter((account) => {
      if (!account) return false;
      if (account.id === "transfers") {
        hasBalanceTransfer = true;
        return false;
      }
      if (account.system && account.id !== "sales-tax-payable") return false;
      return true;
    });

    const typeOrder: Record<string, number> = {
      expenses: 1,
      income: 2,
      assets: 3,
      liabilities: 4,
      equity: 5,
    };
    accounts.sort(
      (a, b) =>
        (typeOrder[a.type[0]] || 99) - (typeOrder[b.type[0]] || 99) ||
        a.name.localeCompare(b.name)
    );

    accounts.unshift({
      name: "Add New Account",
      type: ["new"],
      id: "add_new_account",
    } as any);

    if (hasBalanceTransfer) {
      accounts.unshift({
        name: "Balance Transfer",
        type: ["balance-transfer"],
        id: "transfers",
      } as any);
    }

    return accounts;
  }, [generalLedgerAccounts]);

  const handleSaveAll = async () => {
    if (count === 0) return;

    const update: BatchTransactionUpdate = {};
    const elements: ("gl-account" | "vendor" | "bank-account")[] = [];

    if (selectedAccount) {
      update.gl_account_id = selectedAccount.id;
      update.account = selectedAccount.name;
      elements.push("gl-account");
    }
    if (vendorTouched) {
      update.vendor = selectedVendorId;
      elements.push("vendor");
    }
    if (selectedBankAccount) {
      update.bank_account_id = selectedBankAccount.id;
      update.integration_name = selectedBankAccount.name;
      elements.push("bank-account");
    }

    if (elements.length === 0) return;

    setSaving(true);
    try {
      await batchUpdateTransactions(selectedTransactionIds, update);
      toast.success(`Updated ${count} transaction${count !== 1 ? "s" : ""}`);
      elements.forEach((element) => {
        recordEvent({
          type: "feature-adoption",
          feature: "bulk-edit-panel",
          element,
          count,
        });
      });
      onClose();
    } catch {
      toast.error("Failed to update transactions. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleAccountChange = (
    _event: React.ChangeEvent<{}>,
    value: GeneralLedgerAccountV2 | null
  ) => {
    if (value?.id === "add_new_account") {
      setAddAccountDialogOpen(true);
      return;
    }
    setSelectedAccount(value);
  };

  const handleAddAccountClose = (account?: GeneralLedgerAccountV2) => {
    setAddAccountDialogOpen(false);
    if (account) {
      setSelectedAccount(account);
    }
  };

  return (
    <>
      <AddGeneralLedgerAccountDialog
        open={addAccountDialogOpen}
        onClose={handleAddAccountClose}
      />
      <VendorDialog
        id={vendorDialogOpen ? "add-new-vendor" : undefined}
        onClose={(vendorId) => {
          setVendorDialogOpen(false);
          if (vendorId) {
            setVendorTouched(true);
            setSelectedVendorId(vendorId);
          }
        }}
      />
      <DialogTitle>
        Bulk Edit {count} Transaction{count !== 1 ? "s" : ""}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.header}>
          <Typography variant="body2" color="textSecondary">
            Select values below to apply to all {count} selected transaction
            {count !== 1 ? "s" : ""}. Only changed fields will be updated.
          </Typography>
        </Box>
        {readOnlyCount > 0 && (
          <Box className={classes.notice}>
            <Typography variant="body2">
              {readOnlyCount} Beginning Balance journal
              {readOnlyCount !== 1 ? "s" : ""} in your selection can&rsquo;t be
              edited and will be skipped.
            </Typography>
          </Box>
        )}

        <Box className={classes.fieldGroup}>
          <Autocomplete
            fullWidth
            autoHighlight
            options={glOptions}
            value={selectedAccount}
            onChange={handleAccountChange}
            getOptionLabel={(option) => option?.name || ""}
            getOptionDisabled={(option) => isDisabledAccount(option)}
            groupBy={(option) => {
              if (option.id === "add_new_account" || option.id === "transfers")
                return "";
              const type = option.type?.[0];
              if (type === "expenses") return "Expenses";
              if (type === "income") return "Income";
              if (type === "assets") return "Assets";
              if (type === "liabilities") return "Liabilities";
              if (type === "equity") return "Equity";
              return "Other";
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="General Ledger Account"
                placeholder="Select an account..."
                variant="outlined"
                size="small"
              />
            )}
            renderOption={(option) => {
              const disabled = isDisabledAccount(option);
              if (disabled) {
                return (
                  <Tooltip
                    title="These accounts currently can't be assigned in bulk."
                    placement="right"
                    PopperProps={{ style: { pointerEvents: "none" } }}
                  >
                    <div className={classes.disabledOption}>{option.name}</div>
                  </Tooltip>
                );
              }
              return <span>{option.name}</span>;
            }}
          />

          <Autocomplete
            fullWidth
            autoHighlight
            options={vendorOptions}
            value={selectedVendor}
            getOptionLabel={(option) => option?.name || ""}
            getOptionSelected={(a, b) => a?.id === b?.id}
            onChange={(_event, option) => {
              if (option?.id === ADD_NEW_VENDOR_ID) {
                setVendorDialogOpen(true);
                return;
              }
              if (!option) {
                setVendorTouched(false);
                setSelectedVendorId(undefined);
                return;
              }
              setVendorTouched(true);
              setSelectedVendorId(
                option.id !== NO_VENDOR_ID ? option.id : undefined
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                variant="outlined"
                size="small"
              />
            )}
          />

          <SalePlatformField
            type="all"
            fullWidth
            value={
              selectedBankAccount
                ? {
                    id: selectedBankAccount.id,
                    name: selectedBankAccount.name,
                  }
                : undefined
            }
            onChange={(value) => {
              if (value) {
                setSelectedBankAccount({
                  id: value.id,
                  name: value.name,
                });
              }
            }}
            label="Bank Account"
          />
        </Box>

        <Box className={classes.footer}>
          <Link
            component="button"
            variant="body2"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onOpenCSVBulkEdit();
            }}
          >
            Want to edit from a spreadsheet? Click here.
          </Link>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadableButton
          variant="contained"
          color="primary"
          disabled={
            count === 0 ||
            (!selectedAccount && !vendorTouched && !selectedBankAccount) ||
            saving
          }
          loading={saving}
          onClick={handleSaveAll}
        >
          Save
        </LoadableButton>
      </DialogActions>
    </>
  );
}

function BulkEditPanel({ open, ...contentProps }: BulkEditPanelProps) {
  return (
    <Dialog
      open={open}
      onClose={contentProps.onClose}
      fullWidth
      maxWidth="sm"
      scroll="body"
    >
      <BulkEditPanelContent {...contentProps} />
    </Dialog>
  );
}

export default BulkEditPanel;
